import { endOfMonth, startOfMonth, startOfYear, subMonths, subYears } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GiveBoxEvent } from '@bonusly/common-give-box';
import {
	Alert,
	AlertDescription,
	AlertIcon,
	Box,
	Button,
	CloseButton,
	FormControl,
	FormLabel,
	HStack,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Spinner,
	Text,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { LockClosedOutline } from '@bonusly/ds-icon';
import { useDispatch } from '@bonusly/events';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { DateRange, MonthRangeOption, MonthRangeSelector } from '../../../design-system/date-picker';
import { useCreateSpotlight, useEmployeeSpotlightsForUser, useSpotlightEligibility } from '../../../queries/employee-spotlights';
import { useUser } from '../../../queries/user';
import { EmployeeSpotlight } from '../../../schemas/spotlight';
import { AttachmentCategory, EmployeeSpotlightAttachment } from '../../action-input/lib/entities/attachment';
import { SpotlightGenerationEntryPoint, useSpotlightGenerationAnalytics } from '../hooks/use-spotlight-generation-analytics';
import { GeneratedSpotlightsList } from './generated-spotlights-list';
import { getSpotlightRoute } from './spotlight-navigation';
import { formatUserSpotlightDateRange } from './user-spotlight-date-range';

interface CreateUserSpotlightModalProps {
	isOpen: boolean;
	onClose: () => void;
	targetUserId: string;
	targetUserName?: string;
	onSuccess?: () => void;
	/** Entry point for analytics tracking */
	entryPoint: SpotlightGenerationEntryPoint;
	/** Number of direct reports for analytics (defaults to 0) */
	numberOfDirectReports?: number;
	/** Hide the description text at the top of the create form */
	hideDescription?: boolean;
	/** Show a hint about finding spotlights on the Team hub page */
	showTeamHubHint?: boolean;
	/** Use the rounded celebration-style modal chrome (border radius, custom close button) */
	roundedStyle?: boolean;
}

export const CreateUserSpotlightModal: FC<CreateUserSpotlightModalProps> = ({
	isOpen,
	onClose,
	targetUserId,
	targetUserName,
	onSuccess,
	entryPoint,
	numberOfDirectReports = 0,
	hideDescription = false,
	showTeamHubHint = false,
	roundedStyle = false,
}) => {
	const { t } = useTranslation('spotlight');
	const toast = useToast();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const canAccessSpotlight = usePermission(Permission.CanAccessSpotlight);
	const { data: spotlights, isLoading: isLoadingSpotlights } = useEmployeeSpotlightsForUser(targetUserId, { enabled: canAccessSpotlight });
	const { data: targetUser } = useUser(targetUserId);

	// Analytics tracking
	const { trackListOpened, trackGeneratorOpened, trackGenerationStarted, trackTimeframeAdjusted } = useSpotlightGenerationAnalytics({
		entryPoint,
		employeeId: targetUserId,
		numberOfDirectReports,
	});

	// Handle clicking "Create spotlight" button - track and show form
	const handleCreateNew = useCallback(() => {
		trackGeneratorOpened();
		setShowCreateForm(true);
	}, [trackGeneratorOpened]);

	// Include all spotlights (generating, completed, etc.) in the list view
	const allSpotlights = spotlights || [];
	const hasSpotlights = allSpotlights.length > 0;

	// Date range presets - recalculate when modal opens to ensure fresh dates
	// (component persists between open/close, so we need isOpen as a trigger)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const now = useMemo(() => new Date(), [isOpen]);
	const maxEndMonth = useMemo(() => endOfMonth(now), [now]);
	// Default end month is the last completed month (previous month)
	const defaultEndMonth = useMemo(() => subMonths(maxEndMonth, 1), [maxEndMonth]);
	// Default start month is the user's first month on Bonusly (from their created date)
	// Falls back to 12 months before end month if user data is not available
	const defaultStartMonth = useMemo(() => {
		if (targetUser?.createdAt) {
			return startOfMonth(targetUser.createdAt);
		}
		return startOfMonth(subMonths(defaultEndMonth, 11));
	}, [targetUser?.createdAt, defaultEndMonth]);
	const defaultDateRange: DateRange = useMemo(() => [defaultStartMonth, defaultEndMonth], [defaultStartMonth, defaultEndMonth]);

	// Preset calculations
	const last3MonthsStart = useMemo(() => startOfMonth(subMonths(defaultEndMonth, 2)), [defaultEndMonth]);
	const last6MonthsStart = useMemo(() => startOfMonth(subMonths(defaultEndMonth, 5)), [defaultEndMonth]);
	const last12MonthsStart = useMemo(() => startOfMonth(subMonths(defaultEndMonth, 11)), [defaultEndMonth]);
	const lastYearStart = useMemo(() => startOfYear(subYears(now, 1)), [now]);
	const lastYearEnd = useMemo(() => endOfMonth(subMonths(startOfYear(now), 1)), [now]);

	const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);
	const startMonth = dateRange[0];
	const endMonth = dateRange[1];

	const eligibilityStartTime = useMemo(() => (startMonth ? startOfMonth(startMonth).toISOString() : ''), [startMonth]);
	const eligibilityEndTime = useMemo(() => (endMonth ? endOfMonth(endMonth).toISOString() : ''), [endMonth]);

	const { data: eligibility, isLoading: isLoadingEligibility } = useSpotlightEligibility(targetUserId, eligibilityStartTime, eligibilityEndTime, {
		enabled: canAccessSpotlight && isOpen && !!startMonth && !!endMonth && startMonth <= endMonth && endMonth <= maxEndMonth,
	});

	const isIneligible = eligibility ? !eligibility.eligible : false;
	const isGenerateDisabled =
		!startMonth || !endMonth || startMonth > endMonth || endMonth > maxEndMonth || (isOpen && isLoadingEligibility) || (isOpen && isIneligible);

	// Track if we've initialized state for the current modal open
	const hasInitializedRef = useRef(false);
	// Track original date range for analytics (to determine if preselected)
	const originalDateRangeRef = useRef<DateRange | null>(null);
	// Track number of times user adjusts the date range
	const adjustmentCountRef = useRef(0);

	// Reset the initialization flag when modal closes
	useEffect(() => {
		if (!isOpen) {
			hasInitializedRef.current = false;
			originalDateRangeRef.current = null;
			adjustmentCountRef.current = 0;
		}
	}, [isOpen]);

	// Initialize state when modal opens (only once per open)
	// This prevents resetting state when defaultDateRange changes due to async user data loading
	useEffect(() => {
		if (isOpen && !hasInitializedRef.current) {
			hasInitializedRef.current = true;
			setDateRange(defaultDateRange);
			// Store original date range for analytics comparison
			originalDateRangeRef.current = defaultDateRange;
			// Reset to list view on open (the shouldShowList logic will handle the initial view)
			setShowCreateForm(false);
			// Track modal open
			trackListOpened();
		}
		// Intentionally omitting trackListOpened from deps - we only want to track once
		// when isOpen becomes true, not when the tracking function reference changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, defaultDateRange]);

	const { mutate: createSpotlight, isLoading } = useCreateSpotlight({
		onSuccess: (spotlight) => {
			// If backend returns a completed spotlight immediately, take the user straight to it.
			if (spotlight.status === 'completed') {
				onClose();
				onSuccess?.();
				navigate(getSpotlightRoute(spotlight.id, 'create_spotlight_modal'));
				return;
			}

			toast({
				title: t('createSpotlightModal.toasts.generatingTitle'),
				status: 'info',
				duration: 8000,
			});
			onClose();
			onSuccess?.();
		},
		onError: (error) => {
			toast({
				title: t('createSpotlightModal.toasts.errorTitle'),
				description: error instanceof Error ? error.message : t('createSpotlightModal.toasts.errorFallback'),
				status: 'error',
				duration: 5000,
			});
		},
	});

	const handleGenerate = () => {
		if (!startMonth || !endMonth) return;
		if (endMonth > maxEndMonth) return;

		const startTime = startOfMonth(startMonth);
		const endTime = endOfMonth(endMonth);

		// Determine if user kept the preselected (default) timeframe
		const originalRange = originalDateRangeRef.current;
		const preselectedTimeframe =
			originalRange !== null && originalRange[0]?.getTime() === startMonth.getTime() && originalRange[1]?.getTime() === endMonth.getTime();

		// Track timeframe adjustment if dates were changed
		if (!preselectedTimeframe && originalRange && originalRange[0] && originalRange[1]) {
			trackTimeframeAdjusted({
				originalStart: startOfMonth(originalRange[0]).toISOString(),
				originalEnd: endOfMonth(originalRange[1]).toISOString(),
				finalStart: startTime.toISOString(),
				finalEnd: endTime.toISOString(),
				adjustmentCount: adjustmentCountRef.current,
			});
		}

		// Track generation started
		trackGenerationStarted(preselectedTimeframe);

		createSpotlight({
			user_id: targetUserId,
			start_time: startTime.toISOString(),
			end_time: endTime.toISOString(),
		});
	};

	const handleClose = useCallback(() => {
		setDateRange(defaultDateRange);
		setShowCreateForm(false);
		onClose();
	}, [defaultDateRange, onClose]);

	const handleAddToRecognition = useCallback(
		(spotlight: EmployeeSpotlight) => {
			const spotlightTitle =
				t('generatedSpotlightsModal.spotlightTitle', {
					name: targetUserName || t('viewer.header.defaultName'),
				}) +
				' \u2022 ' +
				formatUserSpotlightDateRange(spotlight.startTime, spotlight.endTime);

			const attachment = new EmployeeSpotlightAttachment({
				category: AttachmentCategory.EmployeeSpotlight,
				id: spotlight.id,
				url: spotlight.url,
				image_url: spotlight.thumbnailUrl ?? spotlight.imageUrl,
				width: spotlight.thumbnailWidth ?? spotlight.imageWidth,
				height: spotlight.thumbnailHeight ?? spotlight.imageHeight,
				title: spotlightTitle,
				alt_text: spotlightTitle,
			});

			setDateRange(defaultDateRange);
			setShowCreateForm(false);
			onClose();
			dispatch(GiveBoxEvent.Open, {
				recipient: {
					id: spotlight.userId,
					displayName: spotlight.user?.displayName ?? targetUserName,
					username: spotlight.user?.username ?? '',
					profilePic: spotlight.user?.profilePicUrl ?? '',
				},
				attachment,
			});
		},
		[dispatch, targetUserName, defaultDateRange, onClose, t]
	);

	const handleDateRangeChange = useCallback((range: DateRange) => {
		setDateRange(range);
		// Increment adjustment count for analytics
		adjustmentCountRef.current += 1;
	}, []);

	// Disable months after maxEndMonth
	const disableMonthIf = useCallback(
		(month: Date): boolean => {
			if (month > maxEndMonth) return true;
			return false;
		},
		[maxEndMonth]
	);

	// Determine whether to show the list view or create form
	const shouldShowList = hasSpotlights && !showCreateForm;

	const headerText = shouldShowList
		? t('generatedSpotlightsModal.title')
		: targetUserName
			? t('createSpotlightModal.titleGenerateWithName', { name: targetUserName })
			: t('createSpotlightModal.titleGenerate');

	return (
		<Modal isCentered={roundedStyle} isOpen={isOpen} onClose={handleClose} size={roundedStyle ? { base: 'full', md: 'xl' } : 'xl'}>
			<ModalOverlay bg="bg.overlayBackdrop" backdropFilter="blur(4px)" />
			<ModalContent maxW={{ base: roundedStyle ? '100%' : '36rem', md: '36rem' }} {...(roundedStyle && { borderRadius: { base: 0, md: rem(24) } })}>
				<ModalHeader {...(roundedStyle && { borderBottom: 'none', color: 'text.default', pt: rem(20) })}>
					<Text fontSize="xl" fontWeight="semibold">
						{headerText}
					</Text>
				</ModalHeader>
				{roundedStyle ? (
					<Box position="absolute" top={4} right={4}>
						<CloseButton borderRadius="full" bg="secondary.bgAlpha" _hover={{ bg: 'secondary.bgAlphaHover' }} onClick={handleClose} w={rem(32)} h={rem(32)} />
					</Box>
				) : (
					<ModalCloseButton />
				)}
				{shouldShowList ? (
					<GeneratedSpotlightsList
						spotlights={allSpotlights}
						isLoading={isLoadingSpotlights}
						targetUserName={targetUserName}
						onCreateNew={handleCreateNew}
						onAddToRecognition={handleAddToRecognition}
					/>
				) : (
					<>
						<ModalBody>
							<VStack spacing={5} align="stretch">
								{!hideDescription && (
									<Text fontSize="sm" color="text.soft">
										{t('createSpotlightModal.description')}
									</Text>
								)}

								{isLoadingEligibility && (
									<HStack spacing={2} color="text.soft">
										<Spinner size="sm" />
										<Text fontSize="sm">{t('createSpotlightModal.loadingEligibility')}</Text>
									</HStack>
								)}

								{isIneligible && (
									<Alert status="warning" variant="subtle">
										<AlertIcon />
										<AlertDescription>
											{t('createSpotlightModal.ineligible', {
												minimumRequired: eligibility?.minimumRequired ?? 5,
												receivedMessagesCount: eligibility?.receivedMessagesCount ?? 0,
											})}
										</AlertDescription>
									</Alert>
								)}

								{hasSpotlights && (
									<Link
										as="button"
										color={roundedStyle ? 'secondary.fg' : 'text.interactive'}
										fontSize="sm"
										alignSelf="start"
										onClick={() => setShowCreateForm(false)}
									>
										{t('createSpotlightModal.viewPrevious')}
									</Link>
								)}

								<FormControl>
									<FormLabel>{t('createSpotlightModal.dateRange.label')}</FormLabel>
									<MonthRangeSelector
										value={dateRange}
										onChange={handleDateRangeChange}
										disableMonthIf={disableMonthIf}
										displayFormat="long"
										isClearable={false}
										w="100%"
										data-testid="spotlight-date-range-selector"
									>
										<MonthRangeOption startMonth={last3MonthsStart} endMonth={defaultEndMonth}>
											{t('createSpotlightModal.dateRange.presets.last3Months')}
										</MonthRangeOption>
										<MonthRangeOption startMonth={last6MonthsStart} endMonth={defaultEndMonth}>
											{t('createSpotlightModal.dateRange.presets.last6Months')}
										</MonthRangeOption>
										<MonthRangeOption startMonth={last12MonthsStart} endMonth={defaultEndMonth}>
											{t('createSpotlightModal.dateRange.presets.last12Months')}
										</MonthRangeOption>
										<MonthRangeOption startMonth={lastYearStart} endMonth={lastYearEnd}>
											{t('createSpotlightModal.dateRange.presets.lastYear')}
										</MonthRangeOption>
									</MonthRangeSelector>
								</FormControl>

								<Box>
									<HStack spacing={2} color="text.soft" align="start">
										<LockClosedOutline size="sm" style={{ marginTop: '2px', flexShrink: 0 }} />
										<VStack spacing={1} align="start">
											<Text fontSize="sm" fontWeight="medium">
												{t('createSpotlightModal.privacyNotice.heading')}
											</Text>
											<Text fontSize="sm">
												{t('createSpotlightModal.privacyNotice.body')}
												{showTeamHubHint && ` ${t('createSpotlightModal.teamHubHint')}`}
											</Text>
										</VStack>
									</HStack>
								</Box>
							</VStack>
						</ModalBody>

						<ModalFooter>
							{!roundedStyle && (
								<Button variant="ghost" mr={3} onClick={handleClose}>
									{t('createSpotlightModal.buttons.cancel')}
								</Button>
							)}
							<Button colorScheme={roundedStyle ? 'secondary' : 'primary'} isLoading={isLoading} isDisabled={isGenerateDisabled} onClick={handleGenerate}>
								{t('createSpotlightModal.buttons.generate')}
							</Button>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
};
